<template>
  <div v-if="isLoading()" class="loader center-middle">
    <LoadingState :size="50" />
  </div>
  <div v-else-if="isErrored()">
    <ErrorState />
  </div>
  <form v-else-if="presets" @submit.prevent="submit">
    <FormMessage :errors="errors" v-if="errors" />
    <div class="d-flex pb-3">
      <div class="pr-4">Environment Time:</div>
      <div class="font-weight-bold">{{ systemTime }}</div>
    </div>
    <FormInput
      name="script"
      v-model="fields.script"
      testId="script-input"
      :label="`${$t('global.script')} *`"
      :errors="validation.fields.script.errors"
      :dirty="validation.fields.script.dirty"
      :disabled="isSubmitting"
    />
    <FormSelect
      name="frequency"
      v-model="fields.frequency"
      testId="frequency-input"
      :label="`${$t('global.frequency')} *`"
      :errors="validation.fields.frequency.errors"
      :dirty="validation.fields.frequency.dirty"
      :disabled="isSubmitting"
    >
      <option v-for="(option, index) in presets.list" :key="index" :value="option.value">
        {{ option.display }}
      </option>
    </FormSelect>
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-add-cron-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable()"
        :loading="isSubmitting"
        data-test="add-cron-button"
      >
        {{ $t("global.add") }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { computed, onMounted, reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import ErrorState from "@/components/display/ItemErrorState";
import FormInput from "@/components/display/form/FormInput";
import FormMessage from "@/components/display/form/FormMessage";
import FormSelect from "@/components/display/form/FormSelect";
import LoadingState from "@/components/display/LoadingState";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { addSiteCron as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "SiteAddCronForm",
  props: {
    resourceName: {
      type: String,
      required: true,
    },
    site: {
      type: Object,
      required: true,
    },
    defaultFrequency: {
      type: String,
      required: false,
      default: "daily",
    },
  },
  components: {
    ErrorState,
    LoadingState,
    FormSelect,
    Btn,
    FormInput,
    FormMessage,
  },
  setup(props, { emit, root }) {
    const presets = computed(() => store.getters["siteCron/getPresets"]);
    const presetsStatus = computed(() => store.getters["siteCron/getPresetsStatus"]);
    const systemTime = computed(() => store.getters["sites/getSystemTime"]?.time);
    const systemTimeStatus = computed(() => store.getters["sites/getSystemTimeStatus"]);

    const cancel = () => emit("close");
    const errors = ref(null);
    const fields = reactive({ frequency: null, script: null });
    const isLoading = () => presetsStatus.value.isLoading || systemTimeStatus.value.isLoading;
    const isErrored = () => presetsStatus.value.isErrored || systemTimeStatus.value.isErrored;
    const isSubmittable = () => !isSubmitting.value && validation.errors.length === 0;
    const isSubmitting = ref(false);
    const { validator } = useValidator();

    const validation = validator(
      fields,
      validationRules,
      {},
      {
        frequency: {
          required: root.$t("validation.frequency-select-required"),
        },
        script: {
          required: root.$t("validation.script-required"),
        },
      }
    );

    const submit = () => {
      errors.value = null;
      validation
        .validate()
        .then(() => {
          const name = props.resourceName;
          const { frequency, script } = fields;
          isSubmitting.value = true;
          store
            .dispatch("siteCron/addTask", {
              id: props.site.id,
              data: {
                frequency,
                script,
              },
            })
            .then(() => {
              store.dispatch("toasts/addToast", {
                variant: "info",
                message: root.$t("toasts.cron-adding-message", { name, script }),
              });
              emit("close");
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: root.$t("toasts.cron-add-error-message", { name, script }),
              });
            })
            .finally(() => {
              isSubmitting.value = false;
            });
        })
        .catch(() => {});
    };

    onMounted(() => {
      store.dispatch("sites/getSystemTime", props.site.id);
      store
        .dispatch("siteCron/getPresets", props.site.id)
        .then(
          (response) =>
            (fields.frequency = response.list.filter(
              (item) => item.value === props.defaultFrequency
            )[0].value)
        );
    });

    return {
      cancel,
      errors,
      fields,
      isErrored,
      isLoading,
      isSubmittable,
      isSubmitting,
      presets,
      submit,
      systemTime,
      validation,
    };
  },
};
</script>

<style lang="scss" scoped>
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
